import { useEffect, useRef, useState } from 'react';
import { mensagemDoErro } from '@/data/http';

export const CARACTERES_MINIMOS = 2;
export const DEBOUNCE_MS = 300;

/**
 * Busca remota com debounce: só consulta com 2+ caracteres, espera o usuário
 * parar de digitar e cancela a consulta anterior (AbortController) — resposta
 * velha nunca sobrescreve a nova.
 */
export function useBuscaRemota<T>(buscar: (termo: string, signal: AbortSignal) => Promise<T[]>) {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState<T[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const buscarRef = useRef(buscar);
  buscarRef.current = buscar;

  const ativa = termo.trim().length >= CARACTERES_MINIMOS;

  useEffect(() => {
    const valor = termo.trim();
    if (valor.length < CARACTERES_MINIMOS) {
      setResultados([]);
      setErro(null);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    const controller = new AbortController();
    const id = window.setTimeout(() => {
      buscarRef
        .current(valor, controller.signal)
        .then((itens) => {
          if (controller.signal.aborted) return;
          setResultados(itens);
          setErro(null);
          setCarregando(false);
        })
        .catch((causa: unknown) => {
          if (controller.signal.aborted || (causa instanceof DOMException && causa.name === 'AbortError')) return;
          setResultados([]);
          setErro(
            mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : 'Não foi possível completar a busca.'),
          );
          setCarregando(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(id);
      controller.abort();
    };
  }, [termo, tentativa]);

  return {
    termo,
    setTermo,
    ativa,
    resultados,
    carregando,
    erro,
    tentarDeNovo: () => setTentativa((v) => v + 1),
  };
}
